import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContractCreateEditState, useContractCreateEditDispatch } from './contract-create-edit-context';
import { ACTION_TYPES } from './contract-create-edit-reducer';
import { IContractAddEditRequest } from '../../../../model/scp/contract/request/contract-add-edit-request';
import { IContractResponse } from '../../../../model/scp/contract/response/contract-response';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import EntityAppService from '../../../../application/platform/scp/entity/entitiy-app-service';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { useBaseData } from '../../../../application/context/base-data-context';
import { useFeedback } from '../../../../hooks/use-feedback';
import { hasErrors } from '../../../../utils/validation';
import { validateContract } from './contract-validation';

const LIST_ROUTE = '/contratos';
const CONTRACT_DOCUMENT_TYPE = 3;

const FIELD_BY_INPUT: Record<string, keyof IContractAddEditRequest> = {
    'select-client': 'clientId',
    'input-process-number': 'processNumber',
    'input-year-contract': 'yearContract',
    'input-number-contract-effort': 'contractEffortNumber',
    'select-cateogry-process': 'categoryProcessId',
    'select-purchase': 'purchaseId',
    'select-unid-entity': 'codBuyerUnit',
    'select-contract-type': 'contractTypeId',
    'select-supplier-type': 'supplierType',
    'input-identfiy-cipi': 'identifyCIPI',
    'input-url-cipi': 'urlCIPI',
    'input-supplier-number-identification': 'supplierNumberIdentification',
    'input-supplier-name': 'supplierName',
    'select-supplier-type-subcontract': 'supplierTypeSubContract',
    'input-supplier-number-identification-subcontract': 'supplierNumberIdentificationSubContract',
    'input-supplier-name-subcontract': 'supplierNameSubContract',
    'input-object': 'object',
    'input-notes': 'notes',
    'input-initial-value': 'initialValue',
    'input-number-payment-installment': 'numberPaymentInstallment',
    'input-payment-installment-value': 'paymentInstallmentlValue',
    'input-global-value': 'globalValue',
    'input-acumulated-value': 'accumulatedValue',
    'select-type-document-id': 'typeDocumentId',
    'input-document-title': 'documentTitle',
};

function toFormRequest(data: IContractResponse): IContractAddEditRequest {
    return {
        ...data,
        numberPaymentInstallment: data.numberOfInstallments,
        paymentInstallmentlValue: data.paymentInstallmentValue,
        identifyCIPI: data.identifyCip,
        urlCIPI: data.urlCipi,
    } as IContractAddEditRequest;
}

export const useContractCreateEditController = () => {
    const navigate = useNavigate();
    const dispatch = useContractCreateEditDispatch();
    const state = useContractCreateEditState();
    const { showError, showSuccess, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);
    const baseData = useBaseData();
    const isEdit = state.contractId !== undefined;

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!baseData.loaded) return;
        dispatch({ type: ACTION_TYPES.setTypeContract, payload: baseData.typeContract });
        dispatch({ type: ACTION_TYPES.setCategoryProcess, payload: baseData.categoryProcess });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseData.loaded]);

    async function loadInitialData() {
        try {
            await withLoading(async () => {
                const [purchases, unidEntity, typeDocument, typeContract, categoryProcess] = await Promise.all([
                    PurchaseAppService.listAllPurchase(),
                    EntityAppService.listUnidEntity(),
                    BaseAppService.listTypeDocument(CONTRACT_DOCUMENT_TYPE),
                    baseData.loaded ? Promise.resolve(baseData.typeContract) : BaseAppService.listTypeContract(),
                    baseData.loaded ? Promise.resolve(baseData.categoryProcess) : BaseAppService.listCategoryProcess(),
                ]);
                dispatch({ type: ACTION_TYPES.setPurchases, payload: purchases });
                dispatch({ type: ACTION_TYPES.setUnidEntity, payload: unidEntity });
                dispatch({ type: ACTION_TYPES.setTypeDocument, payload: typeDocument });
                dispatch({ type: ACTION_TYPES.setTypeContract, payload: typeContract });
                dispatch({ type: ACTION_TYPES.setCategoryProcess, payload: categoryProcess });

                if (state.contractId === undefined) return;
                const data = await ContractAppService.getContract(Number(state.contractId));
                dispatch({ type: ACTION_TYPES.setContractRequest, payload: toFormRequest(data) });
            });
        } catch (error: any) {
            showError('Não foi possível carregar os dados do contrato', error);
        }
    }

    function handleOnChange(e: any) {
        const field = FIELD_BY_INPUT[e.target.name];
        if (!field) return;
        dispatch({ type: ACTION_TYPES.updateContractRequest, payload: { [field]: e.target.value } });
    }

    function handleDateChange(field: keyof IContractAddEditRequest, value: string | null) {
        dispatch({ type: ACTION_TYPES.updateContractRequest, payload: { [field]: value ?? undefined } });
    }

    function handleTabChange(_event: any, index: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex, payload: index });
    }

    function setFileUpload(file: File | undefined) {
        dispatch({ type: ACTION_TYPES.setFile, payload: file });
    }

    function navigateToList() {
        navigate(LIST_ROUTE);
    }

    async function handleSave() {
        if (isSaving) return;
        if (hasErrors(validateContract(state.contractRequest, { isEdit, hasFile: !!state.file }))) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showError('Preencha todos os campos obrigatórios');
            return;
        }

        setIsSaving(true);
        try {
            // A API de inclusão não devolve o contrato criado: em ambos os casos volta para a lista
            await withLoading(() => isEdit
                ? ContractAppService.updateContract(Number(state.contractId), state.contractRequest)
                : ContractAppService.addContract(state.contractRequest, state.file));
            showSuccess(isEdit ? 'Contrato atualizado com sucesso!' : 'Contrato adicionado com sucesso!');
            navigateToList();
        } catch (error: any) {
            showError('Não foi possível salvar o contrato', error);
        } finally {
            setIsSaving(false);
        }
    }

    return {
        isEdit,
        isSaving,
        handleOnChange,
        handleDateChange,
        handleTabChange,
        setFileUpload,
        handleSave,
        navigateToList,
    };
};
